import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Client, PGVerification, ProductGroup } from '@app/core/models/client';
import { FormArray, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { VerificationField } from '@app/core/models/validation-verification';
import { CustomValidators } from '@app/core/utils/custom-validators';
import * as _ from 'lodash';
import {
  TinyEditorConfig,
  TinyEditorService,
} from '@app/core/services/tiny-editor.service';
import { CharacterLimits } from '@app/core/utils/characterLimits';
import { ClientHeaderComponent } from '../../../../shared/client-header/client-header.component';
import { NewGenericPhoneComponent } from '../../../../shared/new-generic-phone/new-generic-phone.component';
import { VerificationPreviewComponent } from '../../../../shared/phone/verification-preview/verification-preview.component';
import { TextInputComponent } from '../../../../shared/text-input/text-input.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormInputStateControlComponent } from '../../../../shared/form-input-state-control/form-input-state-control.component';
import { TinyEditorComponent } from '../../../../shared/tiny-editor/tiny-editor.component';
import { InputInfoBoxComponent } from '../../../../shared/input-info-box/input-info-box.component';

@Component({
    selector: 'app-verification',
    templateUrl: './verification.component.html',
    styleUrls: ['./verification.component.scss'],
    standalone: true,
    imports: [
        ClientHeaderComponent,
        NewGenericPhoneComponent,
        VerificationPreviewComponent,
        TextInputComponent,
        FormsModule,
        ReactiveFormsModule,
        MatTooltipModule,
        FormInputStateControlComponent,
        TinyEditorComponent,
        InputInfoBoxComponent,
    ],
})
export class VerificationComponent implements OnInit {
  @Input() client: Client;
  @Input() productGroup: ProductGroup;
  @Output() validationStateChange: EventEmitter<boolean> = new EventEmitter();

  headerGroups = { verification: true };
  showTooltip: boolean = false;
  // TODO: kinda confusing to have 2 forms in one component...
  verificationConfigForm: FormGroup; // VERIFICATION
  verificationFieldsArray: FormArray; // VALIDATION
  tinyEditorConfig: TinyEditorConfig;
  constructor(private tinyEditorService: TinyEditorService) {}
  ngOnInit() {
    this.initConfigForm();
    this.initVerificationFieldsForm();

    // submit an initial event to broadcast initial validation state
    this.validationStateChange.emit(this.verificationFieldsArray.valid);

    this.verificationFieldsArray.valueChanges.subscribe(() => {
      this.validationStateChange.emit(this.verificationFieldsArray.valid);
    });
    this.initTinyEditorConfig();
  }

  private initTinyEditorConfig() {
    this.tinyEditorConfig = new TinyEditorConfig();
    this.tinyEditorConfig.includeMentionsAutoComplete = true;
    this.tinyEditorConfig.includeAccountMenu = true;
    this.tinyEditorConfig.accountMenuList =
      this.tinyEditorService.accountMenuListShort;
  }

  getVerificationFields(): VerificationField[] {
    return this.verificationFieldsArray.getRawValue() as VerificationField[];
  }

  getVerificationConfig(): PGVerification {
    return this.verificationConfigForm.getRawValue() as PGVerification;
  }

  markAllAsTouched() {
    CustomValidators.markAllAsTouched(this.verificationConfigForm);
    CustomValidators.markAllAsTouched(this.verificationFieldsArray);
  }

  get validationFieldValues(): string[] {
    return _.values(this.verificationFieldsArray.controls);
  }

  // verificationConfigForm Group shape matches the PGVerificationModel in models/client
  private initConfigForm(): void {
    this.verificationConfigForm = new FormGroup({
      body: new FormControl<string>(
        this.productGroup.onboarding.verification.body,
        this.client.feed_enabled
          ? [Validators.maxLength(CharacterLimits.VerificationBodyText)]
          : [],
      ),
      header: new FormControl<string>(
        this.productGroup.onboarding.verification.header,
      ),
      button_label: new FormControl<string>(
        this.productGroup.onboarding.verification.button_label,
        this.client.feed_enabled
          ? [Validators.maxLength(CharacterLimits.VerificationButtonLabel)]
          : [],
      ),
    });
  }

  // formGroups in the verificationFieldsArray
  private initVerificationFieldsForm(): void {
    this.verificationFieldsArray = new FormArray([]);

    _.each(this.client.verification.fields, (field: VerificationField) => {
      // if the client is feed enabled, we want to use the prompt from the product group or
      // fallback to the client config if there isn't one configured
      // otherwise, we want to use the prompt from the clients
      this.verificationFieldsArray.push(
        new FormGroup({
          field: new FormControl<string>(field.field),
          prompt: new FormControl<string>(
            this.client.feed_enabled
              ? this.productGroup.onboarding.verification.fields.find(
                  (item) => item.field === field.field,
                )?.prompt ?? field.prompt
              : field.prompt,
            Validators.required,
          ),
        }),
      );
    });
  }

  getCharacterLimit(form: string) {
    switch (form) {
      case 'verificationBody':
        return CharacterLimits.VerificationBodyText;
      case 'verificationButtonLabel':
        return CharacterLimits.VerificationButtonLabel;
    }
  }
}
